import React, { Component } from 'react';
import { createFragmentContainer, QueryRenderer, graphql } from 'react-relay';
import environment from '../../data/environment';
import { FormattedHTMLMessage } from 'react-intl';
import WidgetMenu from '../../components/WidgetMenu';
import CenteredLoadingAnimation from '../../components/CenteredLoadingAnimation';
import CenteredErrorMessage from '../../components/CenteredErrorMessage';
import demoMode from '../../services/demoMode';
import moment from 'moment';
import _ from 'lodash';

function getExtraVariables(variables, props) {
  if (_.isFunction(variables)) {
    return variables(props);
  } else {
    return variables;
  }
}

export default (query, extraVariables = {}) => (WrappedComponent) => {
  class EnhancedComponent extends Component {
    render() {
      const widget = this.props.widget || {};
      const widgetConfig = widget.config || {};
      const dateRange = this.props.dateRange || {};

      let fromDate = widgetConfig.ignoreDateFilter ? '1970-01-01' : dateRange.from;
      let toDate = widgetConfig.ignoreDateFilter ? '3000-01-01' : dateRange.to;

      if (widgetConfig.showLastXMonths) {
        fromDate = moment()
          .subtract(widgetConfig.showLastXMonths, 'month')
          .startOf('month')
          .format('YYYY-MM-DD');
        toDate = moment()
          .subtract(1, 'month')
          .endOf('month')
          .add(1, 'day')
          .format('YYYY-MM-DD');
      }

      const variables = Object.assign({}, {
        widgetId: widget.id,
        from: fromDate,
        to: toDate,
        deviceIds: this.props.deviceIds,
        filterComments: !!widgetConfig.filterComments,
        demoData: demoMode(),
        questionFilters: this.props.questionFilters && this.props.questionFilters.filter(qf => !!qf.options),
        aggregateQuestions: widgetConfig.aggregateQuestions || [],
        commentExtraColumns: widgetConfig.commentExtraColumns && widgetConfig.commentExtraColumns.map(c => c.questionId),
        groupByLocation: !!this.props.report.groupByLocation,
      }, getExtraVariables(extraVariables, this.props));

      const requiredVariables = _.omit(variables, ['questionFilters', 'commentExtraColumns']);

      const widgetBody = _.some(_.values(requiredVariables), _.isNil) ? (
        <CenteredErrorMessage>
          <FormattedHTMLMessage id="error.missingQueryVariables" />
        </CenteredErrorMessage>
      ) : (
        <QueryRenderer
          environment={environment}
          query={query}
          variables={variables}
          render={({ error, props }) => {

            if (error) {
              console.error(error);

              return (
                <CenteredErrorMessage>
                  <FormattedHTMLMessage id="error.couldNotFetch" />
                  <br /><hr /><div>{error.message}</div>
                  <br /><div>{error.stack}</div>

                </CenteredErrorMessage>
              );
            } else if (props) {
              return (
                <WrappedComponent
                  {...this.props}
                  widget={props.widget}
                />
              );
            } else {
              return <CenteredLoadingAnimation />;
            }
          }}
        />
      );

      return (
        <div style={{ height: this.props.height }}>
          <WidgetMenu
            isAccountAdmin={this.props.isAccountAdmin}
            widget={widget}
            surveys={this.props.surveys}
            report={this.props.report}
            reports={this.props.reports}
          />
          {widgetBody}
        </div>
      );
    }
  }

  const wrappedComponentName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
  EnhancedComponent.displayName = `WithWidgetData(${wrappedComponentName})`;

  return createFragmentContainer(
    EnhancedComponent,
    graphql`
      fragment withWidgetData_report on Report {
        groupByLocation
        ...notifyPhantomWhenMounted_report
        ...CopyWidgetsDialog_report
      }
      fragment withWidgetData_widget on Widget {
        id
        config {
          filterComments
          sliceByQuestion
          aggregateQuestions
          ignoreDateFilter
          showLastXMonths
          commentExtraColumns {
            title
            questionId
          }
        }
        ...WidgetMenu_widget
      }
      fragment withWidgetData_surveys on SurveyConnection {
        ...WidgetMenu_surveys
      }
      fragment withWidgetData_reports on ReportConnection {
        ...WidgetMenu_reports
      }
    `,
  );
};



// WEBPACK FOOTER //
// ./src/components/withWidgetData/withWidgetData.js